import React from 'react'
import PropTypes from 'prop-types'
import {
  Route,
  Switch,
  withRouter
} from 'react-router-dom'
import { withStyles } from '@material-ui/core/styles'
import { AppHeader } from '@ipd/app-header';
import MenuContainer from '../menu/MenuContainer'
import ErrorComponent from '../common/error/ErrorComponent'
import FooterComponent from '../footer/FooterComponent'
import ProfileInfoContainer from '../profile-info/ProfileInfoContainer'
import ConfirmProfileContainer from '../profile-info/ConfirmProfileContainer'
import AddUsersContainer from '../add-users/AddUsersContainer'
import DeactivateUsersContainer from '../deactivate-users/DeactivateUsersContainer'
import ManageUsersContainer from '../manage-users/ManageUsersContainer'
import ManageEmailsContainer from '../manage-emails/ManageEmailsContainer'
import LoadingComponent from '../common/loading/LoadingComponent'
import Fab from '@material-ui/core/Fab'
import Grid from '@material-ui/core/Grid'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ScrollToTop from 'react-scroll-up'
import Raven from 'raven-js'
import settings from '../../libs/settings'
import SolutionsContainer from '../solutions/SolutionsContainer'

const styles = theme => ({
  app: {
    display: 'flex',
    flexDirection: 'column',
    width: 1170,
    minHeight: '100vh',
    margin: '0 auto',
    backgroundColor: 'white'
  },
  content: {
    flexGrow: '1'
  },
  menuContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16,
    paddingLeft: 8,
    minWidth: '18%',
    flexBasis: '18%'
  },
  contentContainer: {
    minWidth: '82%',
    flexBasis: '82%'
  },
  homeLink: {
    textDecoration: 'none',
    color: 'white'
  }
})

class AppErrorBoundary extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      error: null
    }
  }

  componentDidCatch (error, errorInfo) {
    this.setState({error})
    if (settings.isProduction) {
      Raven.captureException(error, {extra: errorInfo})
    }
  }

  render () {
    const { error } = this.state
    const { children } = this.props

    return <React.Fragment>
      {children}
      {error ? <ErrorComponent error={error} /> : null}
    </React.Fragment>
  }
}

class AppComponent extends React.PureComponent {
  render () {
    const {
      classes,
      error,
      authorized,
      history,
      loading,
      contactLoading,
      contactsLoading,
      exclusionsLoading,
      emailPreferencesLoading,
      databasePreferencesLoading
    } = this.props

    const isConfirmPage = window.location.pathname.match(/^\/confirm$/)
    return (
      <div id='App'>
        {!authorized
          ? <ErrorComponent error={error} /> // Authentication/Authorization errors not caught by Raven via AppErrorBoundary
          : <div className={classes.app}>
            <AppErrorBoundary>
              {!isConfirmPage && <AppHeader stage={settings.APP_ENV} />}
              <Grid className={classes.content} container spacing={0}>
                {<Grid className={classes.menuContainer} item xs={isConfirmPage ? 1 : 2}>
                  {!isConfirmPage && <MenuContainer history={history} />}
                </Grid>}
                <Grid className={classes.contentContainer} item xs={10}>
                  <Switch>
                    <Route exact path='/' render={() => <ProfileInfoContainer history={history} />} />
                    <Route exact path='/confirm' render={() => <ConfirmProfileContainer history={history} />} />
                    <Route exact path='/add-users' render={() => <AddUsersContainer history={history} />} />
                    <Route exact path='/deactivate-users' render={() => <DeactivateUsersContainer history={history} />} />
                    <Route exact path='/manage-users' render={() => <ManageUsersContainer history={history} />} />
                    <Route exact path='/solutions' render={() => <SolutionsContainer history={history} />} />
                    {settings.featureToggles['MANAGE_EMAILS'] ? <Route exact path='/manage-emails' render={() => <ManageEmailsContainer history={history} />} /> : null}
                    <Route render={() => <ErrorComponent error={{status: 404, statusText: 'Resource Not Found'}} />} />
                  </Switch>
                </Grid>
                {isConfirmPage && <Grid item xs={1} />}
              </Grid>
              {loading || contactLoading || contactsLoading || exclusionsLoading || emailPreferencesLoading || databasePreferencesLoading ? <LoadingComponent /> : null}
              <FooterComponent />
              <ScrollToTop showUnder={160} duration={1000}>
                <Fab color='primary' aria-label='Scroll to Top'>
                  <ArrowUpwardIcon />
                </Fab>
              </ScrollToTop>
            </AppErrorBoundary>
          </div>
        }
      </div>
    )
  }
}

AppComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  error: PropTypes.object,
  authorized: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  contactLoading: PropTypes.bool,
  contactsLoading: PropTypes.bool,
  exclusionsLoading: PropTypes.bool,
  emailPreferencesLoading: PropTypes.bool,
  databasePreferencesLoading: PropTypes.bool
}

export default withRouter(withStyles(styles)(AppComponent))
